import {
  BodyText,
  Callout,
  Checkbox,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui";
import { CircleCheck } from "lucide-react";
import { useEffect } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useVisitDiagnosesRelevantHistory } from "../network";
import type { VisitClosureFormData, VisitClosureFormInput } from "../schemas";

interface DiagnosisChartingProps {
  patientId: string;
  encounterId: string;
  form: UseFormReturn<VisitClosureFormInput, unknown, VisitClosureFormData>;
  onSelectableCountChange: (count: number) => void;
  onLoadingChange: (isLoading: boolean) => void;
}

export const DiagnosisCharting = ({
  patientId,
  encounterId,
  form,
  onSelectableCountChange,
  onLoadingChange,
}: DiagnosisChartingProps) => {
  const relevantHistoryQuery = useVisitDiagnosesRelevantHistory(
    patientId,
    encounterId,
  );
  const diagnoses = relevantHistoryQuery.data?.visitDiagnoses ?? [];
  const plainNewDiagnoses = diagnoses.filter(
    ({ relevantMedicalHistory }) => relevantMedicalHistory.length === 0,
  );
  const exactDiagnoses = diagnoses.filter(({ relevantMedicalHistory }) =>
    relevantMedicalHistory.some(({ relevance }) => relevance === "MATCH"),
  );
  const similarDiagnoses = diagnoses.filter(
    ({ relevantMedicalHistory }) =>
      relevantMedicalHistory.length > 0 &&
      relevantMedicalHistory.every(({ relevance }) => relevance !== "MATCH"),
  );
  const selectableDiagnoses = [...plainNewDiagnoses, ...similarDiagnoses];
  const { isLoading, isFetching, isError } = relevantHistoryQuery;
  const selectedDiagnosisIds = form.watch("selectedDiagnosisIds");

  useEffect(() => {
    onLoadingChange(isFetching);
  }, [isFetching, onLoadingChange]);

  useEffect(() => {
    onSelectableCountChange(selectableDiagnoses.length);
  }, [onSelectableCountChange, selectableDiagnoses.length]);

  useEffect(() => {
    if (
      !relevantHistoryQuery.data ||
      isFetching ||
      isError ||
      selectedDiagnosisIds !== undefined
    ) {
      return;
    }

    form.setValue(
      "selectedDiagnosisIds",
      plainNewDiagnoses.map(({ condition }) => condition.id),
      {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      },
    );
  }, [
    form,
    isError,
    isFetching,
    plainNewDiagnoses,
    relevantHistoryQuery.data,
    selectedDiagnosisIds,
  ]);

  const toggleDiagnosis = (conditionId: string) => {
    const currentDiagnosisIds = selectedDiagnosisIds ?? [];
    const nextDiagnosisIds = currentDiagnosisIds.includes(conditionId)
      ? currentDiagnosisIds.filter((id) => id !== conditionId)
      : [...currentDiagnosisIds, conditionId];

    form.setValue("selectedDiagnosisIds", nextDiagnosisIds, {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false,
    });
  };

  const renderSelectableDiagnosis = (diagnosis: (typeof diagnoses)[number]) => {
    const { condition, relevantMedicalHistory } = diagnosis;

    return (
      <TableRow key={condition.id} showSeparator={false}>
        <TableCell className="!px-2 !py-2.5 align-top">
          <BodyText
            className="text-[13.5px] font-semibold"
            size="sm"
            textColor="primary"
          >
            {condition.displayName || "Unknown diagnosis"}
          </BodyText>
          {relevantMedicalHistory.length > 0 && (
            <div className="mt-1 flex h-auto flex-col gap-0.5 pl-3">
              <BodyText
                className="mb-px text-[10px] font-bold uppercase tracking-[0.05em]"
                size="xs"
                textColor="tertiary"
              >
                Also on record
              </BodyText>
              {relevantMedicalHistory.map((history) => (
                <div
                  className="flex h-auto items-center gap-[5px]"
                  key={history.condition.id}
                >
                  <CircleCheck
                    aria-hidden
                    className="shrink-0 text-success"
                    size={13}
                  />
                  <BodyText
                    className="text-[11.5px] font-semibold"
                    size="xs"
                    textColor="tertiary"
                  >
                    {history.condition.displayName || "Unknown diagnosis"}
                  </BodyText>
                </div>
              ))}
            </div>
          )}
        </TableCell>
        <TableCell className="!px-2 !py-2.5 align-top text-center whitespace-nowrap">
          <div className="flex h-auto justify-center">
            <Checkbox
              aria-label={`Add ${condition.displayName || "Unknown diagnosis"} to medical record`}
              checked={selectedDiagnosisIds?.includes(condition.id) ?? false}
              onCheckedChange={() => toggleDiagnosis(condition.id)}
            />
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="flex h-auto flex-col gap-[14px]">
      {isLoading && (
        <div className="grid h-auto gap-2">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}
      {isError && (
        <Callout
          title="Unable to load relevant medical history"
          description="You can continue to the next step and finish closing the visit."
          variant="danger"
        />
      )}
      {!isLoading && !isError && (
        <Table className="text-[13px] [&>thead>tr>th]:h-auto [&>thead>tr>th]:border-b [&>thead>tr>th]:border-primary [&>thead>tr>th]:px-2 [&>thead>tr>th]:pt-0 [&>thead>tr>th]:pb-2 [&>tbody>tr>td]:border-b [&>tbody>tr>td]:border-primary [&>tbody>tr:last-child>td]:border-b-0">
          <TableHeader>
            <TableRow showSeparator={false}>
              <TableHead className="text-[11px] font-bold uppercase tracking-[0.04em] text-tertiary">
                Diagnosis
              </TableHead>
              <TableHead className="text-center text-[11px] font-bold uppercase tracking-[0.04em] text-tertiary whitespace-nowrap">
                Add to record
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plainNewDiagnoses.map(renderSelectableDiagnosis)}
            {similarDiagnoses.map(renderSelectableDiagnosis)}
            {exactDiagnoses.map((diagnosis) => {
              const display =
                diagnosis.condition.displayName || "Unknown diagnosis";
              return (
                <TableRow
                  className="bg-secondary"
                  key={diagnosis.condition.id}
                  showSeparator={false}
                >
                  <TableCell className="!px-2 !py-2.5 align-top">
                    <BodyText
                      className="text-[13.5px] font-semibold"
                      size="sm"
                      textColor="secondary"
                    >
                      {display}
                    </BodyText>
                  </TableCell>
                  <TableCell className="!px-2 !py-2.5 align-top text-center whitespace-nowrap">
                    <div
                      aria-label={`${display} is already on record`}
                      className="inline-flex h-auto w-auto items-center justify-center gap-1"
                      title="Already on the medical record"
                    >
                      <CircleCheck
                        aria-hidden
                        className="shrink-0 text-success"
                        size={13}
                      />
                      <BodyText
                        className="text-[11px] font-semibold"
                        size="xs"
                        textColor="tertiary"
                      >
                        On record
                      </BodyText>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
